import { Box } from '@mui/material'
import styled from '@emotion/styled/macro'
import theme from 'theme'
import { inputLabelStyles } from 'ui/components/form-components-v2/inputStates.styles'

const HEADER_HEIGHT = 80

const STEPS_RAIL_WIDTH = 270

const FORM_COLUMN_WIDTH = 614

export const WizardBody = styled(Box)`
  display: flex;
  align-items: stretch;
  width: 100%;
  height: calc(100vh - ${HEADER_HEIGHT}px);
  background: ${theme.colors.pageBackground};
`

export const StepsRail = styled(Box)`
  flex: 0 0 ${STEPS_RAIL_WIDTH}px;
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: ${theme.spacing.xSmall}px;
  padding: ${theme.spacing.medium}px;
  border-right: 1px solid ${theme.colors.strokeSecondary};
  overflow-y: auto;

  .steps-label {
    color: ${theme.colors.textCaption};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.large};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.normal};
    line-height: 22px;
  }

  .step-item {
    display: flex;
    align-items: center;
    gap: ${theme.spacing.md}px;
    width: 100%;
    height: 48px;
    padding: 13px 0;
    border: none;
    border-radius: ${theme.attributes.borderRadius.tertiary};
    background: transparent;
    text-align: left;
    cursor: default;

    &.navigable {
      cursor: pointer;

      &:hover {
        background: ${theme.colors.cardFillSecondary};
      }
    }
  }

  .step-circle {
    display: flex;
    flex: 0 0 32px;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: ${theme.attributes.borderRadius.large};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.xLarge};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 24px;
    letter-spacing: 0.1px;

    &.upcoming {
      border: 1px solid ${theme.colors.tooltipBg};
      color: ${theme.colors.textCaption};
    }

    &.active {
      background: ${theme.colors.cta};
      color: ${theme.colors.pageBackground};
    }

    &.completed {
      border: 1px solid ${theme.colors.btnTertiaryBg};
      background: ${theme.colors.tooltipBg};
      color: ${theme.colors.menuItemText};
    }

    svg path {
      fill: none;
      stroke: currentColor;
    }
  }

  .step-text {
    color: ${theme.colors.textCaption};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.xLarge};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 22px;
    white-space: nowrap;
  }

  .step-item.active .step-text {
    color: ${theme.colors.devicesSecondaryText};
  }
`

export const FormColumn = styled(Box)`
  flex: 0 0 ${FORM_COLUMN_WIDTH}px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  /* Without this the column's automatic minimum size lets a wide child — a
   * select holding every value it was offered — stretch it past its basis and
   * push the rest of the wizard off screen. */
  min-width: 0;
  padding: ${theme.spacing.medium}px;
  border-right: 1px solid ${theme.colors.strokeSecondary};

  .form-scroll {
    flex: 1 1 auto;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
  }

  /* Step-level notices sit a field-gap below the step's own controls. */
  .form-scroll > .section-hint,
  .form-scroll > .section-error {
    margin-top: ${theme.spacing.lg}px;
  }

  .section-hint,
  .section-error {
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.large};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.normal};
    line-height: 20px;
    letter-spacing: 0.25px;
  }

  .section-hint {
    color: ${theme.colors.devicesSecondaryText};
  }

  .section-error {
    color: ${theme.colors.errorLight};
  }

  .form-footer {
    display: flex;
    flex: 0 0 auto;
    gap: ${theme.spacing.xSmall}px;
    padding-top: ${theme.spacing.medium}px;

    .MuiButton-root {
      flex: 1 1 0;
      height: 48px;
      min-width: 0;
      border-radius: ${theme.attributes.borderRadius.tertiary};
      box-shadow: none;
    }
  }
`

export const SectionHeader = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md}px;
  margin-bottom: ${theme.spacing.xLarge}px;

  .section-title {
    color: ${theme.colors.menuItemText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.header};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 36px;
    letter-spacing: 0.1px;
  }

  .section-description {
    color: ${theme.colors.devicesSecondaryText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.large};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.normal};
    line-height: 20px;
    letter-spacing: 0.25px;
  }
`

/* Wraps a step's controls; the hint/error ramps come from FormColumn. */
export const StepBody = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg}px;
  width: 100%;
`

/* One labelled field per step, and the connection step's stack of them. */
export const StepFieldWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  gap: ${theme.spacing.xSmall}px;

  .field-label {
    ${inputLabelStyles}
  }

  /* A label that carries an advisory marker alongside it. */
  .field-label-row {
    display: flex;
    align-items: center;
    gap: ${theme.spacing.half}px;
  }

  .field-warning {
    display: flex;
    align-items: center;
    color: ${theme.colors.warning};
    cursor: default;
  }

  .MuiInputBase-root {
    height: 48px;
  }

  /* The shared select colours its own root, so the design's value colour has to
   * land on the value element to outrank it — placeholder and value alike. */
  .MuiSelect-select {
    color: ${theme.colors.mainSecondary};
    font-size: ${theme.typography.fontSize.large};
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 20px;
    letter-spacing: 0.1px;
  }
`

export const ConnectionFormWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg}px;

  .clear-all-row {
    display: flex;
    justify-content: flex-end;
  }
`

export const RightPane = styled(Box)`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
  padding: ${theme.spacing.medium}px;
  overflow-y: auto;
`

export const InstructionsWrapper = styled(Box)`
  display: flex;
  flex-direction: column;

  .instructions-title {
    color: ${theme.colors.menuItemText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.xxxLarge};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 36px;
    letter-spacing: 0.1px;
  }

  .instructions-description {
    margin-top: ${theme.spacing.md}px;
    color: ${theme.colors.devicesSecondaryText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.large};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.normal};
    line-height: 20px;
    letter-spacing: 0.25px;
  }

  .instructions-steps {
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.medium}px;
    margin-top: ${theme.spacing.xLarge}px;
  }

  .instruction-step {
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.small}px;
    padding-bottom: ${theme.spacing.medium}px;
    border-bottom: 1px solid ${theme.colors.strokeSecondary};

    &:last-of-type {
      border-bottom: none;
      padding-bottom: 0;
    }
  }

  .instruction-step-title {
    color: ${theme.colors.cta};
    font-family: ${theme.typography.fontFamily.primary};
    /* The design's step heading sits between the 16px and 20px scale steps. */
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: 28px;
    letter-spacing: 0.1px;
  }

  .instruction-step-image {
    width: 100%;
    max-height: 475px;
    border-radius: ${theme.attributes.borderRadius.tertiary};
    object-fit: cover;
    object-position: top center;
  }

  .instruction-step-content {
    color: ${theme.colors.devicesSecondaryText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.xLarge};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 24px;
    letter-spacing: 0.1px;

    p {
      margin: 0;
      color: ${theme.colors.menuItemText};
    }

    .section-list-item {
      width: 100%;
      padding-left: ${theme.spacing.half}px;
      color: ${theme.colors.devicesSecondaryText};

      &:first-of-type {
        margin-top: ${theme.spacing.small}px;
      }
    }

    a {
      color: ${theme.colors.cta};

      &:visited,
      &:focus,
      &:active {
        color: ${theme.colors.cta};
      }
    }
  }
`

export const PreviewEmptyStateWrapper = styled(Box)`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.medium}px;

  .empty-state-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 128px;
    height: 128px;
    border: 2px dashed ${theme.colors.strokeSecondary};
    border-radius: ${theme.attributes.borderRadius.large};
    color: ${theme.colors.devicesSecondaryText};

    svg path {
      fill: none;
      stroke: currentColor;
    }
  }

  .empty-state-text {
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.small}px;
    width: 416px;
    max-width: 100%;
    text-align: center;
  }

  .empty-state-title {
    color: ${theme.colors.menuItemText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.header};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 24px;
  }

  .empty-state-description {
    color: ${theme.colors.devicesSecondaryText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.xLarge};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: normal;
  }
`
